import _ from 'lodash';
import { AnyAction } from 'redux';
import { v4 } from 'uuid';

import { CommonActionTypes } from '../../../types/actions/common';
import { UserActionTypes } from '../../../types/actions/user';
import { type BetslipStatus } from '../../../types/betslip';
import { GRStateActionTypes } from '../types/actions/gr';
import { GRChipStatuses } from '../types/chip';
import { GRSkinId, type GRGlobalStatus } from '../types/global';
import { GRNotificationTypes, type GRNotificationState } from '../types/notification';
import { GRRounds, type GRRound, type GRRoundState } from '../types/round';
import { type GROperationUserStatus } from '../types/user';

export type GRUserBetslips = {
  [betslipReference: string]: {
    betslipStatus: BetslipStatus;
    validationDate: number;
    betslipId: number;
    betAmount: number;
    matchIds: Array<number>;
    odds: number;
  };
};

export type GRUserProgressState = {
  SHOE: GRRoundState;
  BALLOON: GRRoundState;
  SHIRT: GRRoundState;
};

export type GRUserState = {
  status: GROperationUserStatus;
  round: GRRound;
  progress: GRUserProgressState;
  notifications: Array<GRNotificationState>;
  betslips: GRUserBetslips;
};

export type GRGlobalState = {
  status?: GRGlobalStatus;
  operationStartDate?: number;
  operationEndDate?: number;
  version?: string | null;
  eligibility?: unknown;
  race?: string;
  skinId?: GRSkinId;
};

export type GRMiscState = {
  tmpNotifications?: Array<GRNotificationState> | null;
  homeVisitedIndex?: number | null;
};

export type GRRulesState = {
  maxPossibleMultiplier?: number;
};

export type GRRoundSingleState = {
  roundId: string;
  requiredPoints: number;
  minStakeMultiplier: number;
  maxStakeMultiplier: number;
};

export type GRRoundsState = {
  [key in Partial<GRRound>]: {
    roundId: string;
    requiredPoints: number;
    minStakeMultiplier: number;
    maxStakeMultiplier: number;
  };
};

export type GRState = {
  user: Partial<GRUserState>;
  global: Partial<GRGlobalState>;
  misc: Partial<GRMiscState>;
  rules: Partial<GRRulesState>;
  rounds: Partial<GRRoundsState>;
};

export const grInitialState: GRState = {
  user: {},
  global: {},
  misc: {
    homeVisitedIndex: null,
    tmpNotifications: null,
  },
  rules: {},
  rounds: {},
};

const generatePreviousNotifState = (nextNotif: GRNotificationState) => {
  if (nextNotif) {
    const { data: nextNotifData, type: notifType } = nextNotif;
    const {
      betslipReference,
      previousStatus: nextStatus,
      previousPoints: nextPoints,
      previousRound: nextRound,
    } = nextNotifData;

    const prevStatusFromNext = {
      [GRChipStatuses.PENDING]: null,
      [GRChipStatuses.LOST]: GRChipStatuses.PENDING,
      [GRChipStatuses.WON]: GRChipStatuses.PENDING,
    };

    const prevRoundFromNext = {
      [GRRounds.SHOE]: undefined,
      [GRRounds.BALLOON]: GRRounds.SHOE,
      [GRRounds.SHIRT]: GRRounds.BALLOON,
    };

    return {
      uuid: v4(),
      type: notifType,
      data: {
        betslipReference,
        previousStatus: nextStatus ? prevStatusFromNext[nextStatus] : undefined,
        nextStatus: nextStatus,

        previousPoints: nextPoints ? null : undefined,
        nextPoints: nextPoints,

        previousRound: nextRound ? prevRoundFromNext[nextRound] : undefined,
        nextRound: nextRound,
      },
    };
  }
};

const actionTypesToReactOn = [
  GRStateActionTypes.UPDATE_STATE,
  GRStateActionTypes.SKIP_CURRENT_TMP_NOTIFICATION,
  GRStateActionTypes.SKIP_ALL_TMP_NOTIFICATIONS,
  GRStateActionTypes.FILL_TEMPORARY_NOTIFICATIONS,
  GRStateActionTypes.SET_HOME_VISITED_INDEX,
  GRStateActionTypes.FLUSH_NOTIFICATIONS,
  CommonActionTypes.RECEIVE_ENTITIES,
  UserActionTypes.SET_DISCONNECTED,
];

export default (state: GRState = grInitialState, action: AnyAction) => {
  const { type, payload } = action;
  const { user, global, misc, rounds, rules } = state;

  if (actionTypesToReactOn.indexOf(type) !== -1) {
    if (type === GRStateActionTypes.UPDATE_STATE) {
      const { userStateMutation } = payload;

      const nextUserState = {};
      _.mergeWith(nextUserState, user, userStateMutation, (objValue, srcValue) => {
        if (_.isArray(srcValue)) {
          return srcValue;
        }
      });

      return {
        ...state,
        user: nextUserState,
        misc: {
          ...misc,
          homeNavigationIsAllowed: misc.homeVisitedIndex !== null,
        },
      };
    }

    if (type === GRStateActionTypes.SKIP_CURRENT_TMP_NOTIFICATION) {
      const { tmpNotifications } = misc;

      if (tmpNotifications) {
        const [, ...next]: Array<unknown> = tmpNotifications;
        const copy = JSON.parse(JSON.stringify(next));

        return {
          ...state,
          misc: {
            ...misc,
            tmpNotifications: copy,
          },
        };
      }
    }

    if (type === GRStateActionTypes.SKIP_ALL_TMP_NOTIFICATIONS) {
      return {
        ...state,
        misc: {
          ...misc,
          tmpNotifications: [],
        },
      };
    }

    if (type === GRStateActionTypes.FLUSH_NOTIFICATIONS) {
      const notifications: Array<GRNotificationState> =
        state?.user?.notifications?.filter(
          (notif) =>
            notif.type === GRNotificationTypes.ROUND_COMPLETE || notif.type === GRNotificationTypes.ROUND_FAILED,
        ) ?? [];

      return {
        ...state,
        user: {
          ...user,
          notifications,
        },
      };
    }

    if (type === GRStateActionTypes.FILL_TEMPORARY_NOTIFICATIONS) {
      const { notifications = [] } = user ?? {};
      const copy = JSON.parse(JSON.stringify(notifications));
      const firstNotif = generatePreviousNotifState(copy[0]);

      return {
        ...state,
        misc: {
          ...misc,
          tmpNotifications: firstNotif ? [firstNotif, ...copy] : copy,
        },
      };
    }

    if (type === UserActionTypes.SET_DISCONNECTED) {
      return {
        ...grInitialState,
        global,
      };
    }

    if (type === GRStateActionTypes.SET_HOME_VISITED_INDEX) {
      const { index } = payload;

      return {
        ...state,
        misc: {
          ...misc,
          homeVisitedIndex: index,
        },
      };
    }

    if (type === CommonActionTypes.RECEIVE_ENTITIES) {
      const { settings } = payload;
      const { giftrace } = settings ?? {};
      const {
        version,
        status,
        eligibility,
        raceId,
        skinId,
        globalRule,
        rounds: incomingRounds,
        operationStartDate,
        operationEndDate,
      } = giftrace ?? {};

      return {
        ...state,
        global: {
          ...global,
          operationStartDate:
            (operationStartDate === null || giftrace === null) && global.operationStartDate
              ? undefined
              : operationStartDate ?? global.operationStartDate,
          operationEndDate:
            (operationEndDate === null || giftrace === null) && global.operationEndDate
              ? undefined
              : operationEndDate ?? global.operationEndDate,
          status: (status === null || giftrace === null) && global.status ? undefined : status ?? global.status,
          version: (version === null || giftrace === null) && global.version ? undefined : version ?? global.version,
          eligibility:
            (eligibility === null || giftrace === null) && global.eligibility
              ? undefined
              : eligibility ?? global.eligibility,
          race: (raceId === null || giftrace === null) && global.race ? undefined : raceId ?? global.race,
          skinId: (skinId === null || giftrace === null) && global.skinId ? undefined : skinId ?? global.skinId,
          // skinId: 'GIFT_RACE',
        },
        rules: (globalRule === null || giftrace === null) && rules ? undefined : globalRule ?? rules,
        rounds: (incomingRounds === null || giftrace === null) && incomingRounds ? undefined : incomingRounds ?? rounds,
      };
    }
  }

  return state;
};
